// @flow
import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { compose, graphql } from 'react-apollo';
import styled from 'styled-components';
import Button from '@atlaskit/button';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import Select, { type OptionsType } from '@atlaskit/select';
import { Field } from '@atlaskit/form';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import { CREATE_VIRTUAL_COMPONENT, CREATE_VIRTUAL_LABEL, JIRA_USERS_FOR_PICKER } from '../../graphql/mutations';
import { Validator } from '../../utils/Validator';
import { ErrorParser } from '../../utils/ErrorParser';
import { ASSIGNEE_TYPES } from '../assets/assigneeTypes.assets';
import { analytics } from '../../services/analytics';
import { ALL_PROJECTS_OPTION } from '../../utils/consts';
import VirtualComponentSelect, { parseVirtualComponentToOption } from '../select/VirtualComponentSelect';
import CustomAsyncSelect from '../select/CustomAsyncSelect';
import LabelSelect from '../select/LabelSelect';
import ProjectSelect, { parseProjectsToOptions } from '../select/ProjectSelect';
import { getJiraProject, getJiraProjects } from '../../services/jira-services';
import useJiraContext from '../../hooks/useJiraContext';

const Footer = styled.div`
    display: flex;
    flex-direction: row-reverse;
    padding: 14px 0 20px;
    button {
        margin: 2px;
    }
`;

type Props = {
    setIsOpen: (isOpen: boolean) => void,
    projectKey?: string,
    onSuccess: Function,
    skipVirtualComponentQueryRefetch?: boolean,
    componentParentsDefaultOptions?: OptionsType[],

    /* props from compose */
    client: any,
    createVirtualComponent: Function,
    getUsersForPicker: Function,
    createVirtualLabel: Function,
    jiraProjectsQuery: any,
};

function VirtualComponentForm({
    setIsOpen,
    projectKey,
    onSuccess,
    skipVirtualComponentQueryRefetch,
    componentParentsDefaultOptions,

    /* props from compose */
    createVirtualComponent,
    getUsersForPicker,
    createVirtualLabel,

    /* rest */
    ...props
}: Props) {
    // form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [assigneeType, setAssigneeType] = useState(ASSIGNEE_TYPES[0]);
    const [projects, setProjects] = useState([]);
    const [componentLead, setComponentLead] = useState(null);
    const [labelValue, setLabelValue] = useState([]);
    const [componentLeadsList] = useState(null);
    const [projectsMenuIsOpen, setProjectsMenuIsOpen] = useState(false);
    const [error, setError] = useState();

    const [parents, setParents] = useState([]);

    const [loading, setLoading] = useState(false);

    const [isProjectsLoading, setIsProjectsLoading] = useState(false);
    const jiraContext = useJiraContext();

    useEffect(() => {
        if (jiraContext.projectKey) {
            getJiraProject(jiraContext.projectKey).then((project) => {
                if (project) {
                    setProjects([
                        {
                            value: project.id,
                            label: project.name,
                            avatar: project.avatarUrls?.['48x48'],
                            obj: project,
                        },
                    ]);
                }
            });
        }
    }, [jiraContext.projectKey]);

    const _setProjects = (newProjects) => {
        setIsProjectsLoading(true);
        if (newProjects.some((p) => p.value === ALL_PROJECTS_OPTION)) {
            getJiraProjects({ maxParallelRequests: 20 }).then((projectsList) => {
                setProjects(parseProjectsToOptions(projectsList));
                setProjectsMenuIsOpen(false);
                setIsProjectsLoading(false);
            });
        } else {
            setProjects(newProjects);
            setIsProjectsLoading(false);
        }
    };

    // submit form
    const _submitForm = async (evt) => {
        evt.preventDefault();

        const uniqueLabels = labelValue.filter((obj) => !obj.id);
        let oldLabels = labelValue.filter((obj) => {
            return !uniqueLabels.some((obj2) => {
                return obj.id === obj2.id;
            });
        });
        let writtenLabels = [];

        if (uniqueLabels?.length > 0) {
            await uniqueLabels.forEach((item) => {
                const newInputLabel = {
                    name: item.label,
                };
                createVirtualLabel({
                    variables: { input: newInputLabel },
                })
                    .then((data) => {
                        writtenLabels = {
                            id: data.data.createVirtualLabel.id,
                            label: data.data.createVirtualLabel.name,
                            value: data.data.createVirtualLabel.name.toLowerCase().replace(/\W/g, ''),
                        };
                        oldLabels = oldLabels.concat(writtenLabels);
                    })
                    .then(() => {
                        const input = {
                            name,
                            description,
                            assigneeType: assigneeType.value,
                            virtualLabels: oldLabels.map((label) => ({
                                id: label.id,
                            })),
                            projects: projects.map((p) => {
                                return {
                                    id: p.obj.id,
                                    key: p.obj.key,
                                    name: p.obj.name,
                                };
                            }),
                        };

                        if (componentLead)
                            input.componentLead = {
                                jiraUserAccountId: componentLead.value,
                                jiraUserDisplayName: componentLead.label,
                                jiraUserAvatarUrl: componentLead.avatarUrl,
                            };

                        if (parents?.length) {
                            input.parents = parents.map((p) => p.value);
                        }

                        const refetchQueries = [];

                        setLoading(true);
                        createVirtualComponent({
                            variables: { input },
                            refetchQueries,
                            awaitRefetchQueries: true,
                        })
                            .then(() => {
                                setIsOpen(false);
                                onSuccess();
                                setLoading(false);
                                analytics.track({
                                    userId: window.license,
                                    event: 'created_component',
                                    timestamp: new Date(),
                                    properties: {
                                        addon_key: process.env.REACT_APP_KEY,
                                        cross_project_component: projects.length > 1 ? 'yes' : 'no',
                                        has_labels: oldLabels.length > 1 ? 'true' : 'false',
                                    },
                                });
                            })
                            .catch((e) => {
                                console.log('full error:', e);
                                setError(e.message || 'Please try again later');
                                setLoading(false);
                            });
                    })
                    .catch((err) => {
                        throw new Error(err);
                    });
            });
        } else {
            const input = {
                name,
                description,
                assigneeType: assigneeType.value,
                virtualLabels: oldLabels.map((label) => ({
                    id: label.id,
                })),
                projects: projects.map((p) => {
                    return {
                        id: p.obj.id,
                        key: p.obj.key,
                        name: p.obj.name,
                    };
                }),
            };

            if (componentLead) {
                input.componentLead = {
                    jiraUserAccountId: componentLead.value,
                    jiraUserDisplayName: componentLead.label,
                    jiraUserAvatarUrl: componentLead.avatarUrl,
                };
            }
            if (parents?.length) {
                input.parents = parents.map((p) => p.value);
            }

            const refetchQueries = [];

            setLoading(true);
            createVirtualComponent({
                variables: { input },
                refetchQueries,
                awaitRefetchQueries: true,
            })
                .then(() => {
                    setLoading(false);
                    analytics.track({
                        userId: window.license,
                        event: 'created_component',
                        timestamp: new Date(),
                        properties: {
                            addon_key: process.env.REACT_APP_KEY,
                            cross_project_component: projects.length > 1 ? 'yes' : 'no',
                        },
                    });
                    setIsOpen(false);
                    onSuccess();
                })
                .catch((e) => {
                    console.log('full error:', e);
                    setError(e.message || 'Please try again later');
                    setLoading(false);
                });
        }
    };

    const _handleSearchUsers = async (value) => {
        if (value) {
            try {
                const res = await getUsersForPicker({ variables: { value: encodeURI(value) } });

                const { jiraUsers } = res.data;

                const mapped = jiraUsers.map((user) => ({
                    value: user.accountId,
                    label: user.displayName,
                    avatarUrl: user.avatarUrl,
                }));
                return mapped;
            } catch (err) {
                console.log('full error:', err);
                setError(err.message || 'Please try again later');
                return [];
            }
        } else {
            return [];
        }
    };

    const { isValidated } = Validator.createVirtualComponent({
        name,
        assigneeType,
        projects,
    });

    const onParentChange = useCallback((parents) => {
        setParents(parents);
    }, []);

    return (
        <ModalTransition>
            <Modal {...props} shouldScrollInViewport onClose={() => setIsOpen(false)}>
                <ModalHeader>
                    <ModalTitle>Create component</ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <form onSubmit={_submitForm}>
                        {error && (
                            <SectionMessage appearance="error">
                                <p>There was an error creating the virtual component:</p>
                                <p style={{ whiteSpace: 'pre-wrap' }}>{ErrorParser.parse(error)}</p>
                            </SectionMessage>
                        )}

                        <Field name="name" isRequired label="Name">
                            {({ fieldProps }) => (
                                <Fragment>
                                    <TextField
                                        {...fieldProps}
                                        style={{ height: 'auto' }}
                                        autoFocus={true}
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </Fragment>
                            )}
                        </Field>
                        <Field name="description" label="Description">
                            {({ fieldProps }) => (
                                <Fragment>
                                    <TextArea
                                        {...fieldProps}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                </Fragment>
                            )}
                        </Field>

                        <Field name="labels" label="Component labels">
                            {({ fieldProps }) => (
                                <LabelSelect
                                    {...fieldProps}
                                    creatable
                                    isMulti
                                    openMenuOnFocus
                                    closeMenuOnSelect={false}
                                    onChange={setLabelValue}
                                    defaultOptions
                                />
                            )}
                        </Field>
                        <Field name="projects" isRequired label="Projects">
                            {({ fieldProps }) => (
                                <ProjectSelect
                                    {...fieldProps}
                                    isMulti
                                    openMenuOnFocus
                                    closeMenuOnSelect={false}
                                    menuIsOpen={projectsMenuIsOpen}
                                    value={projects}
                                    onChange={_setProjects}
                                    defaultOptions
                                    includeAddAll
                                    onMenuOpen={() => setProjectsMenuIsOpen(true)}
                                    onMenuClose={() => setProjectsMenuIsOpen(false)}
                                    isLoading={isProjectsLoading}
                                />
                            )}
                        </Field>
                        <Field name="componentLead" label="Component lead">
                            {({ fieldProps }) => (
                                <Fragment>
                                    <CustomAsyncSelect
                                        {...fieldProps}
                                        placeholder="Type user name"
                                        loadOptions={_handleSearchUsers}
                                        options={componentLeadsList}
                                        value={componentLead}
                                        onChange={(obj) => setComponentLead(obj)}
                                        filterOption={() => {
                                            return true;
                                        }}
                                    />
                                </Fragment>
                            )}
                        </Field>
                        <Field name="defaultAssignee" isRequired label="Default assignee">
                            {({ fieldProps }) => (
                                <Fragment>
                                    <Select
                                        {...fieldProps}
                                        options={ASSIGNEE_TYPES}
                                        value={assigneeType}
                                        classNamePrefix="react-select"
                                        onChange={(obj) => setAssigneeType(obj)}
                                    />
                                </Fragment>
                            )}
                        </Field>

                        {/* parent components */}
                        <Field name="parents" label="Parent components">
                            {({ fieldProps }) => (
                                <VirtualComponentSelect
                                    {...fieldProps}
                                    isMulti
                                    openMenuOnFocus
                                    closeMenuOnSelect={false}
                                    value={parents}
                                    onChange={onParentChange}
                                    defaultOptions={
                                        componentParentsDefaultOptions?.length
                                            ? componentParentsDefaultOptions.map(parseVirtualComponentToOption)
                                            : true
                                    }
                                    menuPlacement="top"
                                />
                            )}
                        </Field>

                        <Footer>
                            <Button
                                type="submit"
                                isDisabled={!isValidated || loading}
                                appearance="primary"
                                iconAfter={loading ? <Spinner size="small" /> : null}
                            >
                                Save
                            </Button>
                            <Button isDisabled={loading} onClick={() => setIsOpen(false)}>
                                Cancel
                            </Button>
                        </Footer>
                    </form>
                </ModalBody>
            </Modal>
        </ModalTransition>
    );
}

const ComposedVirtualComponentForm = compose(
    graphql(CREATE_VIRTUAL_COMPONENT, { name: 'createVirtualComponent' }),
    graphql(JIRA_USERS_FOR_PICKER, { name: 'getUsersForPicker' }),
    graphql(CREATE_VIRTUAL_LABEL, { name: 'createVirtualLabel' }),
)(VirtualComponentForm);

export default ComposedVirtualComponentForm;
